import { isEmpty } from 'lodash'
import { isChristmasTime } from '../is-christmas-time'
import { isEasterTime } from '../is-easter-time'
import { APIAccessGroups, ChartSelectorGroups, FeatureFlags, ShiftModeProductFlags, UseFlags } from '../types'
import { isFlowTheme } from 'src/Libs/utils'

/**
 * The FeatBit SDK returns flag values already converted to their configured type
 * (boolean, number, object, string). These helpers interpret those native values while
 * still tolerating legacy string values (e.g. `'true'`, serialized JSON) during bootstrap.
 */
const asBoolean = (value: unknown): boolean => value === true || value === 'true'

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Parses/casts a raw flag value to a plain object and validates its shape before returning it,
 * so primitives, arrays, and incomplete objects fall back to the caller's default instead of
 * silently masquerading as `T`.
 */
const asObject = <T>(value: unknown, flagName: string, isValid: (value: Record<string, unknown>) => boolean): T | undefined => {
  if (value === undefined || value === null || value === '') return

  let candidate: unknown = value
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value)
    } catch (error) {
      console.error(`Failed to parse ${flagName}:`, error)
      return
    }
  }

  if (!isPlainObject(candidate) || !isValid(candidate)) {
    console.error(`Invalid shape for ${flagName}:`, candidate)
    return
  }

  return candidate as T
}

const isAPIAccessGroups = (value: Record<string, unknown>): boolean =>
  isEmpty(value) || (typeof value.user === 'boolean' && typeof value.company === 'boolean')

const CHART_SELECTOR_KEYS = new Set<keyof ChartSelectorGroups>(['performance', 'poc', 'both'])

/**
 * FeatBit variations may only send a subset of the chart selector keys (e.g. `{ performance: true }`),
 * so only the keys that are present need to be booleans; missing keys are filled in by the caller.
 */
const isChartSelectorGroups = (value: Record<string, unknown>): boolean =>
  Object.keys(value).every(key => CHART_SELECTOR_KEYS.has(key as keyof ChartSelectorGroups) && typeof value[key] === 'boolean')

/**
 * Fills missing keys with `false`. If no chart variant ends up enabled (flag missing, empty or all `false`),
 * falls back to the theme's default chart so the dashboard never renders without charts.
 */
const resolveChartSelector = (chartSelector: Partial<ChartSelectorGroups> | undefined): ChartSelectorGroups => {
  const merged: ChartSelectorGroups = { performance: false, poc: false, both: false, ...chartSelector }

  if (merged.performance || merged.poc || merged.both) return merged

  return { ...merged, performance: !isFlowTheme, poc: isFlowTheme }
}

const isShiftModeProductFlags = (value: Record<string, unknown>): boolean =>
  Object.values(value).every(productEnabled => typeof productEnabled === 'boolean')

const isMinimumRequiredAppVersionForFlow = (value: Record<string, unknown>): boolean =>
  typeof value.iOS === 'string' && typeof value.android === 'string'

/**
 * Maps the raw FeatBit flags into the typed, semantically-named feature flags consumed by the app.
 * JSON flags fall back to safe defaults per flag; tariff flags fail open when the flags errored.
 */
export const mapFlags = (flags: UseFlags, isFeatureFlagsError: boolean): FeatureFlags => {
  const apiAccessGroups = asObject<APIAccessGroups>(flags.rightsManagementApiAccess, 'rightsManagementApiAccess', isAPIAccessGroups)
  const chartSelector = resolveChartSelector(asObject<Partial<ChartSelectorGroups>>(flags.chartSelector, 'chartSelector', isChartSelectorGroups))
  const shiftModeEnabledProducts =
    asObject<ShiftModeProductFlags>(flags.cloudPortalSmartFunctionShiftModeProductEnabled, 'shiftModeEnabledProducts', isShiftModeProductFlags) ?? {}
  const minimumRequiredAppVersionForFlow = asObject<NonNullable<FeatureFlags['minimumRequiredAppVersionForFlow']>>(
    flags.minimumRequiredAppVersionForFlow,
    'minimumRequiredAppVersionForFlow',
    isMinimumRequiredAppVersionForFlow
  ) ?? { iOS: '0.0.0', android: '0.0.0' }

  return {
    isProductFilesEnabled: asBoolean(flags.productFiles),
    isCustomerReferralEnabled: asBoolean(flags.customerReferral),
    isOfflineDisplayEnabled: asBoolean(flags.offlineDisplay),
    isOStromEnabled: isFeatureFlagsError ? true : asBoolean(flags.ostrom),
    isTibberEnabled: isFeatureFlagsError ? true : asBoolean(flags.tibber),
    isLuoxDynamicEnergyTariffEnabled: isFeatureFlagsError ? true : asBoolean(flags.luoxDynamicEnergyTariff),
    isObservabilityAnalyticsEnabled: asBoolean(flags.enableAnalytics),
    isAppRatingEnabled: asBoolean(flags.appRating),
    isCustomerReferralAdministrationEnabled: asBoolean(flags.customerReferralAdministration),
    isShowNewVehicleVendorsEnabled: asBoolean(flags.showNewVehicleVendors),
    chartSelector,
    isMaintenancePageEnabled: asBoolean(flags.maintenancePage),
    isReonicEnabled: asBoolean(flags.reonic),
    isNewEmobiltyRouteEnabled: asBoolean(flags.newEMobiltyRoute),
    apiAccessGroups: apiAccessGroups && !isEmpty(apiAccessGroups) ? apiAccessGroups : undefined,
    isEnforcingIosAppUpdate: asBoolean(flags.enforceIosUpdate),
    isShopMaintenanceModeEnabled: asBoolean(flags.webshopMaintenance),
    isChristmasThemeEnabled: asBoolean(flags.christmasTheme) && isChristmasTime(),
    isEasterThemeEnabled: asBoolean(flags.easterTheme) && isEasterTime(),
    shouldSimulateMobileAppOnWebview: asBoolean(flags.shouldSimulateMobileAppOnWebview),
    isCreateInstallationEnabled: asBoolean(flags.cloudPortalOverviewInstallationCreationEnabled),
    isSgReadyEnabled: asBoolean(flags.isSgReadyEnabled),
    isManualTariffEnabled: asBoolean(flags.isManualTariffConfigurable),
    isUserProfileApiClientEnabled: asBoolean(flags.isUserProfileApiClientEnabled),
    isAi360TariffBannerEnabled: asBoolean(flags.isAi360TariffBannerEnabled),
    isAi360FixedTariffEnabled: asBoolean(flags.isAi360FixedTariffEnabled),
    isAi360SharedTariffEnabled: asBoolean(flags.isAi360SharedTariffEnabled),
    isTariffSettingBoxesEnabled: asBoolean(flags.tariffSettingBoxesEnabled),
    /* Flow flags */
    showDetailedDeviceErrorMessages: asBoolean(flags.showDetailedDeviceErrorMessages),
    useEvseWittyLinkSubTypes: asBoolean(flags.useEvseWittyLinkSubTypes),
    maxWittyLinksPerInstallation: Number(flags.maxWittyLinksPerInstallation || 0),
    useEvseWittyFlex: asBoolean(flags.useEvseWittyFlex),
    dynamicEmsMinimalVersion: flags.dynamicEmsMinimalVersion,
    cloudPortalSmartFunctionShiftModeProductEnabled: shiftModeEnabledProducts,
    minimumRequiredAppVersionForFlow,
    showGuidedTour: asBoolean(flags.showGuidedTour),
    farmConfigurationEnabledForFlow: asBoolean(flags.farmConfigurationEnabledForFlow),
    batterySettingsEnabled: asBoolean(flags.batterySettingsEnabled),
    isExtensionsPageVisibleInFlow: asBoolean(flags.extensionsPageVisibleInFlow),
  }
}
